import React, { useState } from 'react';
import { object } from 'prop-types';
import { apiBaseUrl } from '../../../util/api';
import { IconSpinner } from '../../../components';

import css from './ShippingPriceChecker.module.css';

const ShippingPriceChecker = ({ listing }) => {
  const [zipCode, setZipCode] = useState('');
  const [shippingOptions, setShippingOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedZip, setLastFetchedZip] = useState('');

  const handleZipChange = async e => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(value);

    if (value.length === 5 && value !== lastFetchedZip) {
      const publicData = listing.attributes?.publicData || {};
      const address = publicData?.location?.address;
      const weight = publicData?.shippingWeight;
      const length = publicData?.shippingLength;
      const width = publicData?.shippingWidth;
      const height = publicData?.shippingHeight;
      const uiShipOneItem = publicData?.shippingPriceInSubunitsOneItem;

      if (!address) {
        return;
      }

      const zipCodeMatch = address.match(/\b\d{3}\s?\d{2}\b/);
      const sellerZipCode = zipCodeMatch ? zipCodeMatch[0] : null;

      if (!sellerZipCode) {
        return;
      }

      setLastFetchedZip(value);
      setIsLoading(true);
      setError(null);
      setShippingOptions(null);

      try {
        const response = await fetch(`${apiBaseUrl()}/api/shipping-options`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fromZip: sellerZipCode,
            toZip: value,
            shipmentType: 'parcel',
            weight: weight?.toString(),
            length: length?.toString(),
            width: width?.toString(),
            height: height?.toString(),
            uiShipOneItem: uiShipOneItem,
            shiplinkEnabled: publicData?.shiplinkShipping,
            shippingEnabled: publicData?.shippingEnabled,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setShippingOptions(data || []);
        } else {
          setError('Could not fetch shipping options.');
        }
      } catch {
        setError('Could not fetch shipping options.');
      } finally {
        setIsLoading(false);
      }
    } else if (value.length < 5) {
      setShippingOptions(null);
      setError(null);
    }
  };

  const publicData = listing.attributes?.publicData || {};
  const pickupEnabled = publicData?.pickupEnabled;
  const shippingEnabled = publicData?.shippingEnabled !== false;

  if (!shippingEnabled && !pickupEnabled) {
    return null;
  }

  const hasResults = shippingOptions !== null;

  return (
    <div className={css.container}>
      <div className={css.inlineRow}>
        <label className={css.label}>Check shipping options:</label>
        <input
          className={css.zipInput}
          type="text"
          inputMode="numeric"
          placeholder="Zip code"
          value={zipCode}
          onChange={handleZipChange}
          maxLength={5}
        />
        {isLoading && <IconSpinner className={css.spinner} />}
      </div>

      {hasResults && !isLoading && (
        <ul className={css.optionsList}>
          <li className={css.optionItem}>
            <span className={css.optionService}>Arrange your own</span>
            <span className={css.optionPrice}>0 SEK</span>
          </li>
          {pickupEnabled && (
            <li className={css.optionItem}>
              <span className={css.optionService}>Pickup</span>
              <span className={css.optionPrice}>0 SEK</span>
            </li>
          )}
          {shippingOptions.map(option => (
            <li key={String(option.id)} className={css.optionItem}>
              <span className={css.optionService}>{option.service}</span>
              <span className={css.optionPrice}>
                {option.price} {option.currency}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <p className={css.error}>{error}</p>}
    </div>
  );
};

ShippingPriceChecker.propTypes = {
  listing: object.isRequired,
};

export default ShippingPriceChecker;
